import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { Scale, ChevronLeft, ChevronRight, Pencil, X, Check, XCircle, Loader2, Clock, RefreshCw } from 'lucide-react';
import Swal from "../../utils/apparifySwal";
import ShopSelect from "../../components/ShopSelect";
import { Button } from '../../components/ui';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const getStatusBadge = (status) => {
  switch (status) {
    case 'PENDING':
      return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
    case 'UNDER_REVIEW':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
    case 'APPROVED':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  }
};

const AdminAppeals = () => {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  // Detail modal
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  const fetchAppeals = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize };
      if (statusFilter) params.status = statusFilter;
      const res = await adminApi.getAppeals(params);
      const data = res?.data || res || {};
      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.appeals)
          ? data.appeals
          : [];
      setAppeals(items);

      const total = Number(data.meta?.total || data.pagination?.total || 0);
      const currentPageSize = Number(data.meta?.pageSize || pageSize);
      const computedTotalPages = total > 0 ? Math.ceil(total / currentPageSize) : 1;
      setTotalPages(Math.max(1, computedTotalPages));
    } catch (err) {
      console.error('Failed to fetch appeals', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await adminApi.getAppealCount();
      setPendingCount(res?.data?.count ?? res?.count ?? 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAppeals(); }, [fetchAppeals]);
  useEffect(() => { fetchPendingCount(); }, [fetchPendingCount]);

  const openDetail = async (appeal) => {
    setDetailLoading(true);
    setSelectedAppeal(appeal);
    setReviewerNotes('');
    try {
      const res = await adminApi.getAppealDetail(appeal.id);
      setSelectedAppeal(res.data || appeal);
    } catch {
      /* use the list-level data as fallback */
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResolve = async (decision) => {
    if (!selectedAppeal) return;

    const label = decision === 'APPROVED' ? 'approve' : 'reject';
    const result = await Swal.fire({
      title: `${label.charAt(0).toUpperCase()}${label.slice(1)} this appeal?`,
      text: decision === 'APPROVED'
        ? 'This will lift the business suspension and restore it to active status.'
        : 'The suspension will remain in effect.',
      icon: decision === 'APPROVED' ? 'question' : 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${label}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setResolving(true);
      await adminApi.resolveAppeal(selectedAppeal.id, decision, reviewerNotes || null);
      await Swal.fire('Done', `Appeal ${label}d successfully.`, 'success');
      setSelectedAppeal(null);
      fetchAppeals();
      fetchPendingCount();
    } catch (err) {
      console.error(`Failed to ${label} appeal`, err);
      Swal.fire('Error', `Failed to ${label} appeal.`, 'error');
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  return (
    <div className="admin-data-panel admin-workspace-panel operations-surface overflow-hidden">
      {/* Header */}
      <div className="theme-panel-divider flex flex-col items-center justify-between gap-4 border-b p-6 sm:flex-row">
        <div className="flex items-center gap-3">
          <Scale className="text-indigo-600 dark:text-indigo-400" size={22} />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
            Appeals
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-400">
                {pendingCount} pending
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <ShopSelect
            value={statusFilter}
            onValueChange={(value) => { setStatusFilter(value); setPage(1); }}
            options={STATUS_OPTIONS}
            ariaLabel="Filter appeals by status"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-44 rounded-lg px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          <Button
            onClick={() => { fetchAppeals(); fetchPendingCount(); }}
            icon={RefreshCw}
            loading={loading}
            aria-label="Refresh appeals"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="operations-table-wrap">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="inline-block animate-spin h-8 w-8 text-indigo-500" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading appeals...</p>
          </div>
        ) : appeals.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No appeals found.
          </div>
        ) : (
          <table className="admin-data-table w-full text-left text-sm">
            <thead className="theme-table-head">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Appellant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {appeals.map((a) => (
                <tr key={a.id} className="theme-table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{a.business_name || `Business #${a.business_id}`}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{a.appellant_name || `User #${a.user_id}`}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(a.status)}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(a.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openDetail(a)}
                      className="theme-icon-button"
                      title="View Details"
                    >
                      <Pencil size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-table-pagination flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-40"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-40"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedAppeal && (
        <div className="admin-modal-backdrop theme-modal-backdrop overflow-y-auto">
          <div className="admin-action-modal theme-modal-panel max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="admin-modal-header flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Appeal #{selectedAppeal.id}
                </h3>
                <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(selectedAppeal.status)}`}>
                  {selectedAppeal.status}
                </span>
              </div>
              <button onClick={() => setSelectedAppeal(null)} className="theme-icon-button">
                <X size={24} />
              </button>
            </div>

            {detailLoading ? (
              <div className="admin-modal-body p-8 text-center">
                <Loader2 className="inline-block animate-spin h-6 w-6 text-indigo-500" />
              </div>
            ) : (
              <div className="admin-modal-body p-6 space-y-5">
                {/* Business & Appellant */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Business</h4>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedAppeal.business_name || `#${selectedAppeal.business_id}`}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Appellant</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedAppeal.appellant_name || `User #${selectedAppeal.user_id}`}</p>
                    {selectedAppeal.appellant_email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedAppeal.appellant_email}</p>
                    )}
                  </div>
                </div>

                {/* Suspension info */}
                {(selectedAppeal.suspension_reason || selectedAppeal.suspend_until) && (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-800/40 dark:bg-orange-950/30 p-3">
                    <h4 className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase mb-1">Suspension Details</h4>
                    {selectedAppeal.suspension_reason && (
                      <p className="text-sm text-orange-900 dark:text-orange-200"><span className="font-medium">Reason:</span> {selectedAppeal.suspension_reason}</p>
                    )}
                    {selectedAppeal.suspend_until && (
                      <p className="text-sm text-orange-900 dark:text-orange-200 flex items-center gap-1 mt-1">
                        <Clock size={13} />
                        <span className="font-medium">Until:</span> {new Date(selectedAppeal.suspend_until).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Appeal reason */}
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Appeal Reason</h4>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selectedAppeal.reason}</p>
                  </div>
                </div>

                {/* Resolved info (if already resolved) */}
                {selectedAppeal.reviewer_notes && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reviewer Notes</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedAppeal.reviewer_notes}</p>
                  </div>
                )}

                {/* Actions for PENDING/UNDER_REVIEW appeals */}
                {(selectedAppeal.status === 'PENDING' || selectedAppeal.status === 'UNDER_REVIEW') && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Resolve Appeal</h4>
                    <textarea
                      value={reviewerNotes}
                      onChange={(e) => setReviewerNotes(e.target.value)}
                      placeholder="Reviewer notes (optional)"
                    className="theme-textarea mb-3 min-h-[80px]"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResolve('APPROVED')}
                      disabled={resolving}
                      className="theme-btn-success gap-1.5"
                    >
                      <Check size={16} /> Approve (Lift Suspension)
                    </button>
                    <button
                      onClick={() => handleResolve('REJECTED')}
                      disabled={resolving}
                      className="theme-btn-danger gap-1.5"
                    >
                      <XCircle size={16} /> Reject
                    </button>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-1">
                  <p>Submitted: {formatDate(selectedAppeal.created_at)}</p>
                  {selectedAppeal.resolved_at && <p>Resolved: {formatDate(selectedAppeal.resolved_at)}</p>}
                </div>
              </div>
            )}

            <div className="admin-modal-footer theme-panel-footer flex justify-end p-6">
              <button
                onClick={() => setSelectedAppeal(null)}
                className="theme-btn-neutral"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAppeals;
